/**
 * TransitionPreviewDemo - editor-local two-screen strip that plays the theme's
 * configured screen-to-screen transition on demand (and automatically when a
 * transition setting changes).
 *
 * This is a PREVIEW AID, not the runtime: the real transition runtime lives in
 * web-shell (`ScreenTransitionContainer` + `transitionSpec.ts`), which the
 * frontend workspace intentionally does not import. The demo mirrors the
 * essential motion of the 9 implemented TransitionTypes with lightweight local
 * keyframes and honors the configured duration/easing/direction/delay, so what
 * the user sees here matches the published funnel in character (type, speed,
 * curve, axis), not in pixel-exact layer mechanics.
 *
 * ⚠️ Keep the mapping in `layersFor` and the geometry in `DIRECTION_VECTORS`
 * in sync with web-shell `transitionSpec.ts` (buildTransitionSpec switch) and
 * `screen-transitions.css` (st-kf-* keyframes).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { keyframes } from '@mui/material/styles';
import {
  themeEasingToCss,
  THEME_DIRECTIONAL_TRANSITION_TYPES,
} from '@modules/themes/animation/themeAnimationPresets';
import type { ScreenTransition } from '@segmently/schema-models/v2';

// ── Local keyframes (approximation of web-shell screen-transitions.css) ──
// Movement is var-driven so ONE enter/exit pair serves every direction; the
// per-type vars below mirror the runtime's movementVars(enterFrom, exitTo).
const moveIn = keyframes`from { transform: translate(var(--demo-in-x, 100%), var(--demo-in-y, 0%)); } to { transform: translate(0%, 0%); }`;
const moveOut = keyframes`from { transform: translate(0%, 0%); } to { transform: translate(var(--demo-out-x, -100%), var(--demo-out-y, 0%)); }`;
const moveOutDim = keyframes`from { transform: translate(0%, 0%); filter: brightness(1); } to { transform: translate(var(--demo-out-x, -30%), var(--demo-out-y, 0%)); filter: brightness(0.75); }`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;
// Material fadeThrough: old fades out in the first 35%, new fades+scales in after
const fadeThroughIn = keyframes`0% { opacity: 0; transform: scale(0.92); } 35% { opacity: 0; transform: scale(0.92); } 100% { opacity: 1; transform: scale(1); }`;
const fadeThroughOut = keyframes`0% { opacity: 1; } 35% { opacity: 0; } 100% { opacity: 0; }`;
const scaleIn = keyframes`from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; }`;
const scaleOut = keyframes`from { transform: scale(1); opacity: 1; } to { transform: scale(1.04); opacity: 0; }`;

/** Exported for unit tests — identity map of the demo keyframes. */
export const DEMO_KEYFRAMES = {
  moveIn,
  moveOut,
  moveOutDim,
  fadeIn,
  fadeOut,
  fadeThroughIn,
  fadeThroughOut,
  scaleIn,
  scaleOut,
} as const;

/**
 * Forward-nav geometry (keep in sync with transitionSpec.ts): `direction` is
 * where the visible content moves TOWARD (iOS push = 'left'); the vector is
 * where the ENTERING screen comes FROM.
 */
const DIRECTION_VECTORS: Record<string, { x: string; y: string }> = {
  left: { x: '100%', y: '0%' },
  right: { x: '-100%', y: '0%' },
  up: { x: '0%', y: '100%' },
  down: { x: '0%', y: '-100%' },
};

function scaledPct(pct: string, factor: number): string {
  const n = parseFloat(pct) * factor;
  return `${n === 0 ? 0 : n}%`;
}

const scaledVec = (v: { x: string; y: string }, factor: number) => ({
  x: scaledPct(v.x, factor),
  y: scaledPct(v.y, factor),
});

const inVars = (v: { x: string; y: string }) => ({ '--demo-in-x': v.x, '--demo-in-y': v.y });
const outVars = (v: { x: string; y: string }) => ({ '--demo-out-x': v.x, '--demo-out-y': v.y });

export interface LayerAnimations {
  /** Animation keyframes for the entering (B) layer; null = static visible. */
  enter: ReturnType<typeof keyframes> | null;
  /** Animation for the exiting (A) layer; null = stays put. */
  exit: ReturnType<typeof keyframes> | null;
  /** B renders below A (reveal-style) instead of above. */
  enterBelow?: boolean;
  /** Movement CSS vars consumed by the moveIn/moveOut keyframes. */
  vars: Record<string, string>;
}

/** Effective direction: explicit setting, else the runtime's per-type default. */
export function effectiveDirection(transition?: Pick<ScreenTransition, 'type' | 'direction'> | null): string {
  const type = (transition?.type as string | undefined) ?? 'fade';
  return (transition?.direction as string | undefined)
    ?? (type === 'coverVertical' ? 'up' : 'left');
}

/**
 * Per-type layer mapping, mirroring buildTransitionSpec forward-nav semantics.
 * Exported for unit tests.
 */
export function layersFor(transition?: Pick<ScreenTransition, 'type' | 'direction'> | null): LayerAnimations {
  const type = (transition?.type as string | undefined) ?? 'fade';
  const fwdVec = DIRECTION_VECTORS[effectiveDirection(transition)] ?? DIRECTION_VECTORS.left;
  const fwdInv = scaledVec(fwdVec, -1);

  switch (type) {
    case 'slide':
      // Both screens move together (iOS nav push): old exits fully
      return { enter: moveIn, exit: moveOut, vars: { ...inVars(fwdVec), ...outVars(fwdInv) } };
    case 'push':
      // New pushes old away with parallax: follower dims and lags at 30%
      return { enter: moveIn, exit: moveOutDim, vars: { ...inVars(fwdVec), ...outVars(scaledVec(fwdVec, -0.3)) } };
    case 'cover':
    case 'coverVertical':
      // New slides in over the static old
      return { enter: moveIn, exit: null, vars: inVars(fwdVec) };
    case 'reveal':
      // Old slides away on top, revealing the static new underneath
      return { enter: null, exit: moveOut, enterBelow: true, vars: outVars(fwdInv) };
    case 'fade':
      return { enter: fadeIn, exit: null, vars: {} };
    case 'crossfade':
      return { enter: fadeIn, exit: fadeOut, vars: {} };
    case 'fadeThrough':
      return { enter: fadeThroughIn, exit: fadeThroughOut, vars: {} };
    case 'scale':
      return { enter: scaleIn, exit: scaleOut, vars: {} };
    default:
      return { enter: fadeIn, exit: null, vars: {} };
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Mock screen content (funnel-styled placeholder; theme-agnostic on purpose). */
const MockScreen: React.FC<{ accent: string }> = ({ accent }) => (
  <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#0e1420', p: 1 }}>
    <Box sx={{ height: 5, width: '60%', mx: 'auto', mt: 1.5, mb: 0.75, bgcolor: accent, borderRadius: 3 }} />
    <Box sx={{ height: 4, width: '70%', mx: 'auto', my: 0.5, bgcolor: 'rgba(255,255,255,.35)', borderRadius: 2 }} />
    <Box sx={{ height: 4, width: '50%', mx: 'auto', my: 0.5, bgcolor: 'rgba(255,255,255,.35)', borderRadius: 2 }} />
    <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 8, height: 12, bgcolor: accent, borderRadius: 6 }} />
  </Box>
);

export interface TransitionPreviewDemoProps {
  transition?: ScreenTransition | null;
  /**
   * External play trigger: every CHANGE plays the demo once (the theme gallery's
   * "Play animations" bumps it). The initial value never plays.
   */
  playNonce?: number;
}

export const TransitionPreviewDemo: React.FC<TransitionPreviewDemoProps> = ({ transition, playNonce }) => {
  const [phase, setPhase] = useState<'idle' | 'playing' | 'settled'>('idle');
  const timersRef = useRef<number[]>([]);

  useEffect(() => () => timersRef.current.forEach((t) => window.clearTimeout(t)), []);

  const durationMs = Math.max(transition?.timing?.duration ?? 300, 1);
  const delayMs = Math.max(transition?.timing?.delay ?? 0, 0);
  const easingCss = themeEasingToCss(transition?.timing?.easing as string | undefined);
  const type = transition?.type as string | undefined;
  const layers = layersFor(transition);

  const play = useCallback(() => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
    // Reset to idle first so a replay restarts CSS animations, then play on
    // the next frame.
    setPhase('idle');
    const totalMs = delayMs + durationMs;
    timersRef.current.push(
      window.setTimeout(() => {
        setPhase('playing');
        timersRef.current.push(
          window.setTimeout(() => setPhase('settled'), totalMs + 50),
          window.setTimeout(() => setPhase('idle'), totalMs + 850)
        );
      }, 30)
    );
  }, [durationMs, delayMs]);

  // Autoplay: replay whenever a transition setting changes (never on mount),
  // so the user immediately SEES what the control they just touched does.
  // Debounced via effect cleanup; skipped under prefers-reduced-motion.
  const autoplaySignature = JSON.stringify({
    type: type ?? null,
    direction: (transition?.direction as string | undefined) ?? null,
    durationMs,
    delayMs,
    easing: (transition?.timing?.easing as string | undefined) ?? null,
  });
  const prevAutoplayRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevAutoplayRef.current === autoplaySignature) return undefined;
    const isFirstRun = prevAutoplayRef.current === null;
    prevAutoplayRef.current = autoplaySignature;
    if (isFirstRun || !transition || prefersReducedMotion()) return undefined;
    const timer = window.setTimeout(play, 400);
    return () => window.clearTimeout(timer);
  }, [autoplaySignature, transition, play]);

  // External play (gallery "Play animations"): each nonce change plays once; no transition → nothing.
  const prevPlayNonceRef = useRef(playNonce);
  useEffect(() => {
    if (prevPlayNonceRef.current === playNonce) return;
    prevPlayNonceRef.current = playNonce;
    if (transition) play();
  }, [playNonce, transition, play]);

  const directionSuffix = type && THEME_DIRECTIONAL_TRANSITION_TYPES.has(type)
    ? ` · ${effectiveDirection(transition)}`
    : '';
  const label = transition
    ? `${type}${directionSuffix} · ${durationMs}ms`
    : 'No transition (instant)';

  return (
    <Box
      data-testid="theme-anim-demo"
      data-demo-phase={phase}
      sx={{ mt: 1, p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
      style={{
        ['--demo-duration' as string]: `${durationMs}ms`,
        ['--demo-delay' as string]: `${delayMs}ms`,
        ['--demo-easing' as string]: easingCss,
        ...layers.vars,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Transition preview — {label}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={!transition || phase === 'playing'}
          onClick={play}
          data-testid="theme-anim-demo-play"
          sx={{ textTransform: 'none', py: 0.25 }}
        >
          Play
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {/* Mini viewport: screen A visible at idle; screen B transitions in on play */}
        <Box
          sx={{
            position: 'relative',
            width: 84,
            height: 168,
            borderRadius: 2.5,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'divider',
          }}
        >
          {/* Screen A (exit layer) */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: layers.enterBelow ? 2 : 1,
              animation:
                phase === 'playing' && layers.exit
                  ? `${layers.exit} var(--demo-duration) var(--demo-easing) var(--demo-delay) both`
                  : 'none',
              visibility: phase === 'settled' ? 'hidden' : 'visible',
            }}
          >
            <MockScreen accent="#2f6fed" />
          </Box>
          {/* Screen B (enter layer) */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: layers.enterBelow ? 1 : 2,
              visibility: phase === 'idle' ? 'hidden' : 'visible',
              animation:
                phase === 'playing' && layers.enter
                  ? `${layers.enter} var(--demo-duration) var(--demo-easing) var(--demo-delay) both`
                  : 'none',
            }}
          >
            <MockScreen accent="#22c07a" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TransitionPreviewDemo;
